import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  ApiError,
  fileToBase64,
  readSession,
  uploadBrandAsset,
  type BrandAssetUploadResponse,
} from '../../../lib/api.js';
import {
  DEFAULT_COLORS,
  LOGO_SLOT_DEFS,
  REQUIRED_COPY_KEYS,
  REQUIRED_TYPOGRAPHY_KEYS,
  computeBrandKitCompletion,
  describeHexError,
  readBrandKitDraft,
  setColor,
  setCopy,
  setLogoUrl,
  setTypography,
  validateHexColor,
  type BrandKitColorsDraft,
  type BrandKitDraft,
  type BrandKitLogoSlot,
  type BrandKitTypographyDraft,
} from '../../../lib/brand-kit-form.js';
import { validateTenantSlug } from '../../../lib/tenant-slug.js';

/**
 * Step 3 of the tenant provisioning wizard (D1.5.4) — Brand kit.
 *
 * Required fields, mirroring `brandKitSchema` in
 * `packages/config/src/brand-kit.ts`:
 *   • 4 logo slots (primarySvg, primaryPng, favicon, emailHeader)
 *   • 5 colors (primary, secondary, accent, background, text)
 *   • 2 typography slots (primary, heading)
 *   • 2 copy fields (companyName, supportEmail)
 *
 * Out of scope for D1.5.4 (intake portal D2.2.4 will own these):
 *   • Optional dark-mode logo
 *   • Optional success/warning/danger colors
 *   • Optional copy (tagline, supportPhone, legalEntityName, websiteUrl)
 *   • Font URL repeater
 *   • Terminology repeater
 *   • Live typography rendering with downloaded fonts
 *   • Drag-and-drop / Google Fonts autosuggest
 *
 * Uploads POST a base64 JSON body to `/api/platform/tenants/brand-asset`,
 * which validates per-asset (SVG parseable, PNG ≥ 1024px, favicon
 * 16/32 px) and returns the public Vercel Blob URL. The URL is persisted
 * into `payload.brandKit.logo[slot]` so the draft round-trips.
 */

type UploadStatus =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'uploaded' }
  | { kind: 'error'; message: string };

type UploaderClient = (req: {
  apiUrl: string;
  token: string;
  body: {
    tenantSlug: string;
    version: number;
    kind: (typeof LOGO_SLOT_DEFS)[number]['kind'];
    mimeType: string;
    bytesBase64: string;
  };
}) => Promise<BrandAssetUploadResponse>;

const defaultUploader: UploaderClient = ({ apiUrl, token, body }) =>
  uploadBrandAsset(apiUrl, token, body);

type StepBrandProps = {
  payload: Record<string, unknown>;
  updatePayload: (patch: Record<string, unknown>) => void;
  config: ClientConfig;
  /** Test seam — overrides the network upload path. */
  uploader?: UploaderClient;
};

const NEW_TENANT_KIT_VERSION = 1;

export function StepBrand({
  payload,
  updatePayload,
  config,
  uploader = defaultUploader,
}: StepBrandProps): JSX.Element {
  const slug = typeof payload.slug === 'string' ? payload.slug.trim() : '';
  const slugOk = useMemo(() => validateTenantSlug(slug).ok, [slug]);
  const draft = useMemo(() => readBrandKitDraft(payload), [payload]);
  const [statuses, setStatuses] = useState<Record<BrandKitLogoSlot, UploadStatus>>(() => ({
    primarySvg: { kind: 'idle' },
    primaryPng: { kind: 'idle' },
    favicon: { kind: 'idle' },
    emailHeader: { kind: 'idle' },
  }));
  const seqRef = useRef<Record<BrandKitLogoSlot, number>>({
    primarySvg: 0,
    primaryPng: 0,
    favicon: 0,
    emailHeader: 0,
  });

  // Seed default colors on first mount so the draft round-trips through the
  // brand-kit schema even if the admin never touches the swatches. Admins
  // can still overwrite — this only fires while colors is undefined.
  useEffect(() => {
    if (!draft.colors) {
      const next = applyDefaults(draft);
      updatePayload({ brandKit: next });
    }
    // run-once seed; subsequent edits flow through onColorChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = async (
    slot: BrandKitLogoSlot,
    file: File,
  ): Promise<void> => {
    if (!slugOk) {
      setStatuses((s) => ({
        ...s,
        [slot]: { kind: 'error', message: 'Finish Step 1 with a valid slug first.' },
      }));
      return;
    }
    const def = LOGO_SLOT_DEFS.find((d) => d.slot === slot)!;
    const session = readSession();
    if (!session.token) {
      setStatuses((s) => ({
        ...s,
        [slot]: { kind: 'error', message: 'Sign in again to upload assets.' },
      }));
      return;
    }
    const seq = ++seqRef.current[slot];
    setStatuses((s) => ({ ...s, [slot]: { kind: 'uploading' } }));
    try {
      const bytesBase64 = await fileToBase64(file);
      const res = await uploader({
        apiUrl: config.deployment.apiUrl,
        token: session.token,
        body: {
          tenantSlug: slug,
          version: NEW_TENANT_KIT_VERSION,
          kind: def.kind,
          mimeType: def.mimeType,
          bytesBase64,
        },
      });
      if (seqRef.current[slot] !== seq) return; // stale response — drop
      const next = setLogoUrl(readBrandKitDraft(payload), slot, res.url);
      updatePayload({ brandKit: next });
      setStatuses((s) => ({ ...s, [slot]: { kind: 'uploaded' } }));
    } catch (e) {
      if (seqRef.current[slot] !== seq) return;
      const message =
        e instanceof ApiError
          ? `Upload failed (${e.status}). ${e.message}`
          : 'Upload failed — check your connection and retry.';
      setStatuses((s) => ({ ...s, [slot]: { kind: 'error', message } }));
    }
  };

  const onFileChange = (slot: BrandKitLogoSlot) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const file = e.target.files?.[0];
      if (!file) return;
      void handleFile(slot, file);
    };

  const onColorChange = (key: keyof BrandKitColorsDraft) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const next = setColor(readBrandKitDraft(payload), key, e.target.value);
      updatePayload({ brandKit: next });
    };

  const onTypographyChange = (key: keyof BrandKitTypographyDraft) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const next = setTypography(readBrandKitDraft(payload), key, e.target.value);
      updatePayload({ brandKit: next });
    };

  const onCopyChange = (key: 'companyName' | 'supportEmail') =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const next = setCopy(readBrandKitDraft(payload), key, e.target.value);
      updatePayload({ brandKit: next });
    };

  const completion = useMemo(() => computeBrandKitCompletion(draft), [draft]);
  const completionPct =
    completion.total === 0 ? 0 : Math.round((completion.completed / completion.total) * 100);

  return (
    <div className="ferro-form" aria-label="Brand kit — Step 3">
      {/* UX-2 — within-step progress so the admin can see how many required
          fields remain. Re-derived from the draft on every render; no
          separate state. The schema enforces the same set at commit, so
          a 100% bar guarantees the step parses cleanly. */}
      <div
        className="ferro-form__progress"
        role="status"
        aria-label="Brand kit completion"
        data-testid="step-brand-progress"
        style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '1rem' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.85rem',
            color: 'var(--ferro-text-muted, rgba(0,0,0,0.65))',
          }}
        >
          <span>
            {completion.completed} of {completion.total} required fields complete
          </span>
          <span data-testid="step-brand-progress-pct">{completionPct}%</span>
        </div>
        <div
          aria-hidden
          style={{
            height: 6,
            background: 'rgba(0,0,0,0.08)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <div
            data-testid="step-brand-progress-fill"
            style={{
              height: '100%',
              width: `${completionPct}%`,
              background:
                completionPct === 100
                  ? 'var(--ferro-color-success, #2a9d56)'
                  : 'var(--ferro-color-primary, #b87333)',
              transition: 'width 200ms ease',
            }}
          />
        </div>
      </div>

      {!slugOk ? (
        <p role="alert" className="ferro-form__error" data-testid="step-brand-no-slug">
          Finish Step 1 with a valid slug before uploading brand assets — the
          upload path is anchored to the tenant slug.
        </p>
      ) : null}

      <section className="ferro-field" aria-labelledby="step-brand-logo-heading">
        <h2 id="step-brand-logo-heading" className="ferro-field__label">
          Logo &amp; favicon
        </h2>
        <p className="ferro-muted">
          Files upload to <code>tenants/{slug || '«slug»'}/v1/</code>. SVG must be
          parseable, primary PNG ≥ 1024px, favicon 16×16 or 32×32 px.
        </p>

        {LOGO_SLOT_DEFS.map((def) => {
          const status = statuses[def.slot];
          const url = draft.logo?.[def.slot];
          return (
            <LogoSlotRow
              key={def.slot}
              slot={def.slot}
              label={def.label}
              hint={def.hint}
              accept={def.accept}
              required={def.required}
              url={url}
              status={status}
              disabled={!slugOk}
              onFileChange={onFileChange(def.slot)}
            />
          );
        })}
      </section>

      <section className="ferro-field" aria-labelledby="step-brand-colors-heading">
        <h2 id="step-brand-colors-heading" className="ferro-field__label">
          Brand colors
        </h2>
        <p className="ferro-muted">
          6-digit hex like <code>#62A2C3</code>. Defaults reflect Ferro tokens
          and can be overridden per tenant.
        </p>
        <div className="ferro-wizard__brand-colors">
          {(['primary', 'secondary', 'accent', 'background', 'text'] as const).map((key) => {
            const value = draft.colors?.[key] ?? DEFAULT_COLORS[key];
            const v = validateHexColor(value);
            const inputId = `step-brand-color-${key}`;
            return (
              <div className="ferro-field" key={key}>
                <label htmlFor={inputId} className="ferro-field__label">
                  {colorLabel(key)}
                </label>
                <div
                  className="ferro-wizard__brand-color-row"
                  style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
                >
                  <span
                    aria-hidden
                    data-testid={`step-brand-color-swatch-${key}`}
                    style={{
                      display: 'inline-block',
                      width: 24,
                      height: 24,
                      borderRadius: 4,
                      border: '1px solid rgba(0,0,0,0.18)',
                      background: v.ok ? v.value : 'transparent',
                    }}
                  />
                  <input
                    id={inputId}
                    type="text"
                    name={`brand-color-${key}`}
                    inputMode="text"
                    autoCapitalize="none"
                    spellCheck={false}
                    className="ferro-field__input"
                    value={value}
                    onChange={onColorChange(key)}
                    aria-invalid={!v.ok ? true : undefined}
                    aria-describedby={!v.ok ? `${inputId}-error` : undefined}
                    data-testid={`step-brand-color-input-${key}`}
                  />
                </div>
                {!v.ok ? (
                  <span
                    id={`${inputId}-error`}
                    role="alert"
                    className="ferro-form__error"
                  >
                    {describeHexError(v.reason)}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className="ferro-field" aria-labelledby="step-brand-typography-heading">
        <h2 id="step-brand-typography-heading" className="ferro-field__label">
          Typography
        </h2>
        {REQUIRED_TYPOGRAPHY_KEYS.map((key) => {
          const inputId = `step-brand-typography-${key}`;
          return (
            <div className="ferro-field" key={key}>
              <label htmlFor={inputId} className="ferro-field__label">
                {typographyLabel(key)}
              </label>
              <input
                id={inputId}
                type="text"
                name={`brand-typography-${key}`}
                className="ferro-field__input"
                value={draft.typography?.[key] ?? ''}
                onChange={onTypographyChange(key)}
                placeholder={typographyPlaceholder(key)}
              />
            </div>
          );
        })}
      </section>

      <section className="ferro-field" aria-labelledby="step-brand-copy-heading">
        <h2 id="step-brand-copy-heading" className="ferro-field__label">
          Brand copy
        </h2>
        {REQUIRED_COPY_KEYS.map((key) => {
          const inputId = `step-brand-copy-${key}`;
          return (
            <div className="ferro-field" key={key}>
              <label htmlFor={inputId} className="ferro-field__label">
                {copyLabel(key)}
              </label>
              <input
                id={inputId}
                type={key === 'supportEmail' ? 'email' : 'text'}
                name={`brand-copy-${key}`}
                className="ferro-field__input"
                value={draft.copy?.[key] ?? ''}
                onChange={onCopyChange(key)}
                placeholder={copyPlaceholder(key)}
              />
            </div>
          );
        })}
      </section>
    </div>
  );
}

type LogoSlotRowProps = {
  slot: BrandKitLogoSlot;
  label: string;
  hint: string;
  accept: string;
  required: boolean;
  url?: string;
  status: UploadStatus;
  disabled: boolean;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

function LogoSlotRow({
  slot,
  label,
  hint,
  accept,
  required,
  url,
  status,
  disabled,
  onFileChange,
}: LogoSlotRowProps): JSX.Element {
  const inputId = `step-brand-logo-${slot}`;
  return (
    <div className="ferro-field" data-testid={`step-brand-logo-row-${slot}`}>
      <label htmlFor={inputId} className="ferro-field__label">
        {label}{' '}
        {required ? (
          <span className="ferro-field__required" aria-label="required">*</span>
        ) : (
          <span className="ferro-field__optional ferro-muted" aria-label="optional">
            (optional)
          </span>
        )}
      </label>
      <p className="ferro-muted">{hint}</p>
      <input
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={onFileChange}
        data-testid={`step-brand-logo-input-${slot}`}
      />
      {status.kind === 'uploading' ? (
        <span className="ferro-muted" data-testid={`step-brand-logo-status-${slot}`}>
          Uploading…
        </span>
      ) : null}
      {status.kind === 'uploaded' || (status.kind === 'idle' && url) ? (
        <span
          className="ferro-muted"
          data-testid={`step-brand-logo-status-${slot}`}
        >
          {status.kind === 'uploaded' ? 'Uploaded.' : 'Saved.'}
        </span>
      ) : null}
      {status.kind === 'error' ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid={`step-brand-logo-error-${slot}`}
        >
          {status.message}
        </span>
      ) : null}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="ferro-muted"
          data-testid={`step-brand-logo-url-${slot}`}
        >
          {url}
        </a>
      ) : null}
    </div>
  );
}

function applyDefaults(draft: BrandKitDraft): BrandKitDraft {
  return {
    ...draft,
    colors: { ...DEFAULT_COLORS, ...(draft.colors ?? {}) },
  };
}

function colorLabel(key: keyof BrandKitColorsDraft): string {
  switch (key) {
    case 'primary':
      return 'Primary';
    case 'secondary':
      return 'Secondary';
    case 'accent':
      return 'Accent';
    case 'background':
      return 'Background';
    case 'text':
      return 'Text';
  }
}

function typographyLabel(key: keyof BrandKitTypographyDraft): string {
  switch (key) {
    case 'primaryFontFamily':
      return 'Body font family';
    case 'headingFontFamily':
      return 'Heading font family';
  }
}

function typographyPlaceholder(key: keyof BrandKitTypographyDraft): string {
  switch (key) {
    case 'primaryFontFamily':
      return 'Open Sans';
    case 'headingFontFamily':
      return 'Playfair Display';
  }
}

function copyLabel(key: 'companyName' | 'supportEmail'): string {
  switch (key) {
    case 'companyName':
      return 'Company name';
    case 'supportEmail':
      return 'Support email';
  }
}

function copyPlaceholder(key: 'companyName' | 'supportEmail'): string {
  switch (key) {
    case 'companyName':
      return 'Happy Tail Happy Dog';
    case 'supportEmail':
      return 'hello@happytailhappydog.com';
  }
}
